import { useEffect, useState, type FormEvent } from 'react'
import { Gauge, Loader2, Save, Trash2 } from 'lucide-react'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { Field, Modal } from '@/shared/ui/Modal'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { listUsers } from '@/features/users/api'
import { Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import {
  clearOverlay, deleteStatistic, publishAgent, publishMood, publishRisk, recordStatistic,
} from './api'
import { RISK_LABEL, RISK_ORDER, type UnitDetailResponse } from '@/shared/api/types'

/** Kenya has held general elections every five years since 1992. */
const ELECTION_YEARS = [2027, 2022, 2017, 2013, 2007, 2002, 1997, 1992]

/**
 * Record the register and turnout figures for one unit and one election year.
 *
 * This is what makes the turnout column non-blank. `turnoutPct` is deliberately
 * not an input: the backend computes it from the two counts, and rejects a
 * supplied value that disagrees by more than half a point — two fields that must
 * agree, where one silently wins, is a data-quality bug with a long fuse.
 */
export function StatisticsDialog({
  detail, onClose, onSaved,
}: {
  detail: UnitDetailResponse | null
  onClose: () => void
  onSaved: () => void
}) {
  const [year, setYear] = useState(2022)
  const [registered, setRegistered] = useState<number | ''>('')
  const [cast, setCast] = useState<number | ''>('')
  const [rejected, setRejected] = useState<number | ''>('')
  const [source, setSource] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const rows = detail?.statistics ?? []

  useEffect(() => {
    if (!detail) return
    setError(null)
    // Prefill from the most recent year already on file, so correcting one figure
    // is not a re-type of all five.
    const latest = rows[0]
    setYear(latest?.electionYear ?? 2022)
    setRegistered(latest?.registeredVoters ?? '')
    setCast(latest?.votesCast ?? '')
    setRejected(latest?.rejectedVotes ?? '')
    setSource(latest?.source ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail])

  function load(y: number) {
    const row = rows.find((r) => r.electionYear === y)
    setYear(y)
    setRegistered(row?.registeredVoters ?? '')
    setCast(row?.votesCast ?? '')
    setRejected(row?.rejectedVotes ?? '')
    setSource(row?.source ?? '')
  }

  const computed = registered !== '' && cast !== '' && registered > 0
    ? Number(((cast / registered) * 100).toFixed(2))
    : null

  async function submit(e: FormEvent) {
    e.preventDefault()
    const u = detail?.unit
    if (!u) return
    setBusy(true); setError(null)
    try {
      await recordStatistic(u.id, year, {
        registeredVoters: registered === '' ? null : registered,
        votesCast: cast === '' ? null : cast,
        rejectedVotes: rejected === '' ? null : rejected,
        source: source.trim() || null,
      })
      onSaved()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove(y: number) {
    const u = detail?.unit
    if (!u) return
    setBusy(true); setError(null)
    try {
      await deleteStatistic(u.id, y)
      onSaved()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const overCast = registered !== '' && cast !== '' && cast > registered

  return (
    <Modal
      open={Boolean(detail)}
      onClose={onClose}
      width="lg"
      title={`Election statistics — ${detail?.unit.name ?? ''}`}
      description="Register and turnout per election year. Upserted on (unit, year), so re-recording a year corrects it."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Close</button>
          <button type="submit" form="stat-form" className="erp-btn erp-btn-primary"
                  disabled={busy || overCast}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save {year}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        {rows.length > 0 && (
          <div className="border border-border rounded-md overflow-hidden">
            <ErpTable
              head={<><Th>Year</Th><Th>Registered</Th><Th>Cast</Th><Th>Turnout</Th>
                     <Th className="axis-hide-sm">Source</Th><Th /></>}
            >
              {rows.map((r) => (
                <tr key={r.electionYear} className="hover:bg-muted/40">
                  <Td className="erp-metric">
                    <button type="button" className="erp-link" onClick={() => load(r.electionYear)}>
                      {r.electionYear}
                    </button>
                  </Td>
                  <Td className="erp-metric">{fmtNumber(r.registeredVoters ?? null)}</Td>
                  <Td className="erp-metric">{fmtNumber(r.votesCast ?? null)}</Td>
                  <Td className="erp-metric">
                    {r.turnoutPct == null ? '—' : `${r.turnoutPct}%`}
                  </Td>
                  <Td className="axis-hide-sm text-erp-xs">{r.source ?? '—'}</Td>
                  <Td>
                    <button
                      type="button"
                      className="axis-icon-btn erp-btn"
                      title={`Delete ${r.electionYear}`}
                      onClick={() => void remove(r.electionYear)}
                      disabled={busy}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </Td>
                </tr>
              ))}
            </ErpTable>
          </div>
        )}

        <form id="stat-form" onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Election year" required>
              <select value={year} onChange={(e) => load(Number(e.target.value))}
                      className="erp-input w-full">
                {ELECTION_YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}{rows.some((r) => r.electionYear === y) ? ' · on file' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Source" hint="Where the figures came from — quoted back in the audit trail.">
              <input value={source} onChange={(e) => setSource(e.target.value)}
                     maxLength={96} className="erp-input w-full"
                     placeholder="IEBC 2022 Presidential Results" />
            </Field>
            <Field label="Registered voters">
              <input type="number" min={0} value={registered}
                     onChange={(e) => setRegistered(e.target.value === '' ? '' : Number(e.target.value))}
                     className="erp-input w-full" />
            </Field>
            <Field label="Votes cast" error={overCast ? 'More votes than registered voters.' : undefined}>
              <input type="number" min={0} value={cast}
                     onChange={(e) => setCast(e.target.value === '' ? '' : Number(e.target.value))}
                     className={cn('erp-input w-full', overCast && 'border-red-500')} />
            </Field>
            <Field label="Rejected votes">
              <input type="number" min={0} value={rejected}
                     onChange={(e) => setRejected(e.target.value === '' ? '' : Number(e.target.value))}
                     className="erp-input w-full" />
            </Field>
            <Field label="Turnout" hint="Computed from the two counts — not an input.">
              <input
                value={computed == null ? '—' : `${computed}%`}
                disabled
                className="erp-input w-full opacity-70 erp-metric"
              />
            </Field>
          </div>

          <p className="text-erp-xs text-muted-foreground">
            A parent with no figures of its own gets turnout <strong>summed</strong> from the
            units beneath it — never averaged across them, because a 200-voter ward and a
            20,000-voter ward do not count equally. The table marks a summed figure with ≈.
          </p>
        </form>
      </div>
    </Modal>
  )
}

/**
 * Publish an assessment onto a unit's row: mood, risk, or the assigned agent.
 *
 * These write `geography_unit_overlay` through `UnitOverlayPort` — the same port
 * the future `intel` and field features will use. The permission is
 * `geography:overlay`, separate from `geography:update`, because publishing a risk
 * assessment and renaming a ward are different powers.
 *
 * Clearing a facet is a first-class action, not "set it to low": null means nobody
 * has looked, and the table renders that differently on purpose.
 */
export function AssessmentDialog({
  detail, onClose, onSaved,
}: {
  detail: UnitDetailResponse | null
  onClose: () => void
  onSaved: () => void
}) {
  const [tab, setTab] = useState<'mood' | 'risk' | 'agent'>('risk')
  const [mood, setMood] = useState(50)
  const [trend, setTrend] = useState('STABLE')
  const [risk, setRisk] = useState('LOW')
  // Value AND label together: the picker cannot resolve an id back to a name, and the
  // overlay already carries the name it stored, so an existing assignment shows a person
  // rather than a uuid before anybody types anything.
  const [agent, setAgent] = useState<{ value: string; label: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const u = detail?.unit
  const o = u?.overlay
  const isStation = u?.unitType === 'POLLING_STATION'

  useEffect(() => {
    if (!detail) return
    setError(null)
    setTab('risk')
    setMood(o?.moodIndex ?? 50)
    setTrend(o?.moodTrend ?? 'STABLE')
    setRisk(o?.riskLevel ?? 'LOW')
    setAgent(o?.agentUserId
      ? [{ value: o.agentUserId, label: o.agentName || o.agentUserId }]
      : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null)
    try {
      await fn()
      onSaved()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!u) return
    if (tab === 'mood') {
      await run(() => publishMood(u.id, { index: mood, trendCode: trend as never }))
    } else if (tab === 'risk') {
      await run(() => publishRisk(u.id, { levelCode: risk as never }))
    } else {
      // Only the id. The server reads the name and phone off the account, so the row
      // cannot disagree with User Management.
      await run(() => publishAgent(u.id, { userId: agent[0]!.value }))
    }
  }

  const published = tab === 'mood' ? o?.moodIndex != null
    : tab === 'risk' ? Boolean(o?.riskLevel)
    : Boolean(o?.agentUserId)

  const TABS = [
    { id: 'risk' as const, label: 'Risk' },
    { id: 'mood' as const, label: 'Mood' },
    { id: 'agent' as const, label: 'Agent' },
  ]

  return (
    <Modal
      open={Boolean(detail)}
      onClose={onClose}
      title={`Assessment — ${u?.name ?? ''}`}
      description="Published onto this unit's row for display, sorting and filtering. The system of record stays with whoever produced the assessment."
      footer={
        <>
          {published && (
            <button
              type="button"
              className="erp-btn mr-auto"
              disabled={busy}
              onClick={() => { if (u) void run(() => clearOverlay(u.id, tab)) }}
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear {tab}
            </button>
          )}
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button
            type="submit" form="assess-form" className="erp-btn erp-btn-primary"
            disabled={busy || (tab === 'agent' && agent.length === 0)}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gauge className="h-3.5 w-3.5" />}
            Publish
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        <div className="flex items-center gap-1 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'h-9 px-4 text-erp-sm border-b-2 -mb-px',
                tab === t.id
                  ? 'border-chocolate text-chocolate font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form id="assess-form" onSubmit={submit} className="space-y-4">
          {tab === 'risk' && (
            <Field label="Risk level" required hint="Sorted by severity in the table, never alphabetically.">
              <div className="flex gap-2 flex-wrap">
                {RISK_ORDER.slice().reverse().map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRisk(r)}
                    className={cn(
                      'erp-btn',
                      risk === r && 'border-chocolate bg-chocolate-soft/50 text-chocolate font-medium',
                    )}
                  >
                    {RISK_LABEL[r]}
                  </button>
                ))}
              </div>
            </Field>
          )}

          {tab === 'mood' && (
            <>
              <Field label={`Mood index — ${mood}`} required
                     hint="0–100 favourability. 50 is neutral, so the colour reads from distance to the midpoint rather than an arbitrary threshold.">
                <input
                  type="range" min={0} max={100} step={1}
                  value={mood}
                  onChange={(e) => setMood(Number(e.target.value))}
                  className="w-full"
                />
              </Field>
              <Field label="Trend">
                <select value={trend} onChange={(e) => setTrend(e.target.value)}
                        className="erp-input w-full">
                  <option value="RISING">Rising</option>
                  <option value="STABLE">Stable</option>
                  <option value="FALLING">Falling</option>
                </select>
              </Field>
            </>
          )}

          {tab === 'agent' && (
            isStation ? (
              // The modal body scrolls (`overflow-y-auto`), which CLIPS the picker's
              // absolutely-positioned result list. Risk and Mood are tall enough that it
              // never shows; this tab is one field, so the list would open into nothing.
              // Reserving the height is cheaper and far less risky than portalling a
              // dropdown that four other screens already depend on.
              <div className="min-h-[19rem]">
              <Field
                label="Agent"
                required
                hint="The Axis user acting as this station's agent. Their name and phone come from their account — there is nothing to type here, and nothing to keep in step."
              >
                <AsyncMultiSelect
                  label="Agent"
                  emptyLabel="Search by name or phone"
                  placeholder="Type a name or phone number"
                  selected={agent}
                  // Single-select built on the multi picker: keep only the last chosen, so a
                  // second pick replaces rather than adds. Same idiom as the programme
                  // scope-unit picker, which has the identical one-of-many shape.
                  onChange={(next) => setAgent(next.slice(-1))}
                  search={async (term) => {
                    const page = await listUsers({ q: term } as never, 0, 20)
                    return page.content.map((usr) => ({
                      value: usr.id,
                      label: usr.name || usr.phone || usr.id,
                      sub: usr.phone,
                    }))
                  }}
                />
              </Field>
              </div>
            ) : (
              <Note tone="info">
                An agent is assigned to a polling station. This unit's agent column is
                <strong> coverage</strong> — how many stations beneath it have one — and is
                maintained by the server, not set here. Open a station to assign someone.
              </Note>
            )
          )}
        </form>
      </div>
    </Modal>
  )
}
